/**
 * The line ripple behind the sign-in card — Claude Design's copy of 14 Oct 2026 (00:05 UTC): the Sign up / Log in
 * page draws "the same ripple as the closing section" of the public landing page, in place of the 2026-10-11 moving
 * line field (`line-field.tsx`, retired), under a radial spotlight in the page's ink (`.pd-spotlight`).
 *
 * The portal's copy of web-public's `src/site/line-ripple.ts` (the apps never import each other), number for number:
 * a square grid of short lines, each turning once clockwise every 3 s on cubic-bezier(0.7, 0, 0, 1), the wave
 * starting on the centre diagonal and spreading to the top-left and bottom-right corners; 30 lines across (16 on a
 * screen under 768px), never closer than 52px (41px). No pointer reaction.
 *
 * Decoration only (`aria-hidden`). It runs only while the canvas is on screen, at no more than twice the CSS pixels,
 * as one path a frame; with `prefers-reduced-motion: reduce` it draws one still frame (every line upright) and never
 * animates, and it follows a change of that setting. The colour is the canvas's CSS `color`, a token of
 * `public-design-tokens.css`, so no colour is written here.
 */
import { useEffect, useRef, type ReactNode } from "react";

/** The design's numbers: a turn every 3 s, 1/15 s between neighbouring diagonals, a phone under 768px. */
export const RIPPLE = Object.freeze({
  loopSecond: 3,
  stepSecond: 1 / 15,
  phoneWidth: 768,
  /** Lines across the section, and the closest they may sit, on a wide screen and on a phone. */
  across: 30,
  minGap: 52,
  acrossPhone: 16,
  minGapPhone: 41,
  /** A line's half-length and thickness, as fractions of the gap. */
  half: 0.415,
  thickness: 0.05,
  /** The sharpest the canvas draws: twice the CSS pixels. */
  maxPixelRatio: 2,
});

/** cubic-bezier(0.7, 0, 0, 1), as a lookup table of 1,025 points solved once (the design's `nlCfEase`). */
export const rippleEaseOf: (progress: number) => number = (() => {
  const x = (s: number): number => 2.1 * (1 - s) * (1 - s) * s + s * s * s;
  const y = (s: number): number => s * s * (3 - 2 * s);
  const table = new Float32Array(1025);
  for (let index = 0; index <= 1024; index += 1) {
    let low = 0;
    let high = 1;
    for (let step = 0; step < 30; step += 1) {
      const middle = (low + high) / 2;
      if (x(middle) < index / 1024) low = middle;
      else high = middle;
    }
    table[index] = y((low + high) / 2);
  }
  return (progress: number): number => {
    const at = progress * 1024;
    const index = Math.floor(at);
    if (index >= 1024) return 1;
    const from = table[index] ?? 0;
    const to = table[index + 1] ?? 1;
    return from + (to - from) * (at - index);
  };
})();

export interface RippleLine {
  /** Centre, in CSS pixels from the canvas's top-left corner. */
  readonly x: number;
  readonly y: number;
  /** Seconds this line's turn waits behind the centre diagonal's. */
  readonly delay: number;
}

export interface RippleGrid {
  readonly column: number;
  readonly row: number;
  readonly gap: number;
  readonly half: number;
  readonly thickness: number;
  readonly line: readonly RippleLine[];
}

/** The grid for a canvas `width` × `height` CSS pixels, centred, as the design lays it out. */
export function rippleGridOf(width: number, height: number): RippleGrid {
  const gap = width < RIPPLE.phoneWidth ? Math.max(RIPPLE.minGapPhone, width / RIPPLE.acrossPhone) : Math.max(RIPPLE.minGap, width / RIPPLE.across);
  const column = Math.max(1, Math.round(width / gap));
  const row = Math.max(1, Math.round(height / gap));
  const x0 = (width - (column - 1) * gap) / 2;
  const y0 = (height - (row - 1) * gap) / 2;
  const centreColumn = (column - 1) / 2;
  const centreRow = (row - 1) / 2;
  const line: RippleLine[] = [];
  for (let r = 0; r < row; r += 1) {
    for (let c = 0; c < column; c += 1) {
      line.push({ x: x0 + c * gap, y: y0 + r * gap, delay: RIPPLE.stepSecond * Math.abs(c - centreColumn + (r - centreRow)) });
    }
  }
  return { column, row, gap, half: gap * RIPPLE.half, thickness: gap * RIPPLE.thickness, line };
}

/** A line's angle in radians at `second` (0 = upright); always upright when `still`. */
export function rippleAngleOf(second: number, delay: number, still: boolean): number {
  if (still) return 0;
  const loop = RIPPLE.loopSecond;
  const progress = ((((second - delay) % loop) + loop) % loop) / loop;
  return rippleEaseOf(progress) * Math.PI * 2;
}

/** Draws and runs the ripple on `canvas`; returns the function that stops it. */
export function mountLineRipple(canvas: HTMLCanvasElement): () => void {
  const context = canvas.getContext("2d");
  if (context === null) return () => {};
  const reduced = globalThis.matchMedia("(prefers-reduced-motion: reduce)");
  let width = 0;
  let height = 0;
  let ratio = 1;
  let grid: RippleGrid | null = null;
  let colour = "";
  let frame = 0;
  let onScreen = false;

  const setup = (): void => {
    width = canvas.offsetWidth;
    height = canvas.offsetHeight;
    ratio = Math.min(globalThis.devicePixelRatio || 1, RIPPLE.maxPixelRatio);
    canvas.width = Math.max(1, Math.round(width * ratio));
    canvas.height = Math.max(1, Math.round(height * ratio));
    grid = rippleGridOf(width, height);
    colour = globalThis.getComputedStyle(canvas).color;
  };
  const draw = (second: number): void => {
    if (grid === null) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, width, height);
    context.strokeStyle = colour;
    context.lineWidth = grid.thickness;
    context.lineCap = "butt";
    context.beginPath();
    for (const line of grid.line) {
      const angle = rippleAngleOf(second, line.delay, reduced.matches);
      const dx = Math.sin(angle) * grid.half;
      const dy = Math.cos(angle) * grid.half;
      context.moveTo(line.x - dx, line.y + dy);
      context.lineTo(line.x + dx, line.y - dy);
    }
    context.stroke();
  };
  const tick = (now: number): void => {
    frame = globalThis.requestAnimationFrame(tick);
    draw(now / 1000);
  };
  const play = (): void => {
    if (frame === 0 && onScreen && !reduced.matches) frame = globalThis.requestAnimationFrame(tick);
  };
  const pause = (): void => {
    if (frame !== 0) globalThis.cancelAnimationFrame(frame);
    frame = 0;
  };

  const visibility = new globalThis.IntersectionObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (entry === undefined) return;
    onScreen = entry.isIntersecting;
    if (onScreen) play();
    else pause();
  });
  const size = new globalThis.ResizeObserver(() => {
    setup();
    draw(globalThis.performance.now() / 1000);
  });
  const onMotion = (): void => {
    if (reduced.matches) {
      pause();
      draw(0);
    } else play();
  };

  setup();
  draw(globalThis.performance.now() / 1000);
  visibility.observe(canvas);
  size.observe(canvas);
  reduced.addEventListener("change", onMotion);

  return () => {
    pause();
    visibility.disconnect();
    size.disconnect();
    reduced.removeEventListener("change", onMotion);
  };
}

/** The canvas behind the page, mounted once; it stops when the page goes. */
export function LineRipple(): ReactNode {
  const host = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = host.current;
    if (canvas === null) return;
    return mountLineRipple(canvas);
  }, []);
  return <canvas ref={host} className="pd-ripple" aria-hidden="true" />;
}
